import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { getApiHostHeaders, requireApiBaseUrl } from '@/lib/api-url';
import { appendImageToFormData, prepareImageForUpload } from '@/lib/image-upload';
import api from '@/services/api';
import { useAuthStore } from '@/store/authStore';
import {
  PublicProfileConfig,
  PublicProfileView,
  SlugAvailability,
} from '@/types/public-profile';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFetchErrorMessage } from '@/utils/api-error';

export const publicProfileKeys = {
  all: ['public-profile'] as const,
  mine: () => [...publicProfileKeys.all, 'mine'] as const,
  view: (slug: string) => [...publicProfileKeys.all, 'view', slug] as const,
};

/** Configuração do perfil público do prestador logado. */
export function useMyPublicProfile(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();

  return useQuery({
    queryKey: publicProfileKeys.mine(),
    queryFn: async () => {
      const response = await api.get<PublicProfileConfig>('/provider/me/public-profile');
      return response.data;
    },
    enabled: isHydrated && (options?.enabled ?? true),
  });
}

export function useUpdateMyPublicProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<PublicProfileConfig>) => {
      const response = await api.patch<PublicProfileConfig>(
        '/provider/me/public-profile',
        data,
      );
      return response.data;
    },
    // Aplica na hora (switches não podem esperar o round-trip) e desfaz se falhar.
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: publicProfileKeys.mine() });
      const previous = queryClient.getQueryData<PublicProfileConfig>(
        publicProfileKeys.mine(),
      );
      if (previous) {
        queryClient.setQueryData(publicProfileKeys.mine(), { ...previous, ...data });
      }
      return { previous };
    },
    onError: (_error, _data, context) => {
      if (context?.previous) {
        queryClient.setQueryData(publicProfileKeys.mine(), context.previous);
      }
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(publicProfileKeys.mine(), updated);
    },
  });
}

export async function fetchSlugSuggestion(): Promise<string> {
  const response = await api.get<{ slug: string }>(
    '/provider/me/public-profile/slug-suggestion',
  );
  return response.data.slug;
}

export async function checkSlugAvailability(slug: string): Promise<SlugAvailability> {
  const response = await api.get<SlugAvailability>(
    '/provider/me/public-profile/slug-availability',
    { params: { slug } },
  );
  return response.data;
}

export function slugUnavailableMessage(reason?: SlugAvailability['reason']): string {
  switch (reason) {
    case 'SLUG_INVALID_FORMAT':
      return 'Use de 3 a 30 caracteres: letras minúsculas, números e hífen.';
    case 'SLUG_RESERVED':
      return 'Esse link é reservado pelo sistema.';
    case 'SLUG_TAKEN':
      return 'Esse link já está em uso.';
    default:
      return 'Link indisponível.';
  }
}

/** Upload de logo ou capa (multipart, igual ao avatar). */
export function useUploadBrandImage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ kind, imageUri }: { kind: 'logo' | 'cover'; imageUri: string }) => {
      const formData = new FormData();
      // A capa já sai do editor recortada, reduzida e em JPEG.
      const image =
        kind === 'cover'
          ? { uri: imageUri, name: 'cover.jpg', type: 'image/jpeg' }
          : await prepareImageForUpload(imageUri, 'logo', 'logo');
      await appendImageToFormData(formData, 'file', image);

      const API_URL = requireApiBaseUrl().replace(/\/$/, '');
      const token = useAuthStore.getState().token;

      const response = await fetch(`${API_URL}/provider/me/public-profile/${kind}`, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
          ...getApiHostHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(getFetchErrorMessage(response.status, errorData, 'Erro ao enviar imagem.'));
      }

      return (await response.json()) as PublicProfileConfig;
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(publicProfileKeys.mine(), updated);
    },
  });
}

/** Página pública por slug — funciona sem login. */
export function usePublicProfileView(slug: string | undefined) {
  return useQuery({
    queryKey: publicProfileKeys.view(slug ?? ''),
    queryFn: async () => {
      const response = await api.get<PublicProfileView>(
        `/public-profile/${encodeURIComponent(slug!)}`,
      );
      return response.data;
    },
    enabled: !!slug,
    retry: (failureCount, error: any) => {
      // 404 = perfil indisponível; não adianta tentar de novo
      if (error?.response?.status === 404) return false;
      return failureCount < 2;
    },
  });
}

/** Monta o link wa.me com mensagem pré-preenchida e utm_source. */
export function buildWhatsappLink(
  whatsappNumber: string,
  utmSource: 'profile' | 'qr_code' | 'post_generator' = 'profile',
): string {
  const number = whatsappNumber.length <= 11 ? `55${whatsappNumber}` : whatsappNumber;
  const message = 'Olá! Vi seu perfil na Controlapag!';
  return `https://wa.me/${number}?text=${encodeURIComponent(message)}&utm_source=${utmSource}`;
}
